import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * <RetryBanner> — surfaces a recoverable load failure with a single retry CTA.
 *
 * Use for ANY top-level resource fetch failure (Today bundle, Inbox, Calls,
 * Payments, Reading, etc.) so the user sees a clear "this failed, retry"
 * affordance instead of a silent empty list.
 *
 * Props:
 *   message  — short user-facing copy. Default: "We couldn't reach the server."
 *   onRetry  — fn called when the user clicks Retry.
 *   testId   — auto-prefixes child testids.
 */
export function RetryBanner({
  message = "We couldn't reach the server.",
  hint = 'Check your connection — we’ll pick up where you left off.',
  onRetry,
  testId = 'retry-banner',
}) {
  return (
    <div
      data-testid={testId}
      role="alert"
      aria-live="polite"
      className="mx-4 my-3 px-4 py-3 rounded-2xl flex items-start gap-3"
      style={{
        background: 'var(--pd-danger-soft)',
        border: '1px solid var(--pd-danger-base)',
        color: 'var(--pd-text-primary)',
      }}
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--pd-danger-base)' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold" style={{ color: 'var(--pd-danger-base)' }}>
          {message}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: 'var(--pd-text-secondary)' }}>
          {hint}
        </div>
      </div>
      {onRetry ? (
        <button
          data-testid={`${testId}-retry`}
          onClick={onRetry}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
          style={{ background: 'var(--pd-danger-base)', color: 'white' }}
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      ) : null}
    </div>
  );
}

export default RetryBanner;
